import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseFormStateReturn,
} from "react-hook-form";

import ConnectForm from "@/components/organisms/Form/ConnectForm/ConnectForm";
import { cn } from "@/styles/cn";

import { HelpText } from "./HelpText";

export type ControllerField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  render: (prop: {
    field: ControllerRenderProps<TFieldValues, TName>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<TFieldValues>;
    idHelp: string;
    id: string;
  }) => React.ReactElement;
};

export type RenderFooterProps = {
  error?: string;
  helpText?: string;
  invalid: boolean;
  idHelp: string;
  fieldValue: unknown;
};

export type BaseControllerFieldProps = {
  id?: string;
  label?: string;
  className?: string;
  labelClassName?: string;
  name: string;
  required?: boolean;
  helpText?: string;
  layout?: "vertical" | "horizontal";
  invalid?: boolean;
  warning?: boolean;
  /**
   * Custom render for the footer area (replaces default HelpText).
   * Receives field value, error state, and helpText to render custom footer.
   */
  renderFooter?: (props: RenderFooterProps) => React.ReactNode;
};

/**
 * ### Field base "abstracta" para Integra Componentes UI con  <a href="https://legacy.react-hook-form.com/advanced-usage/#ConnectForm" target="_blank">react-hook-form</a>
 *
 * No deberia ser usada de forma directa, ver ejemplos de implementación en algún *Field
 *
 */
export const BaseControllerField = ({
  render,
  id,
  name,
  className,
  label,
  labelClassName,
  required,
  helpText,
  invalid,
  warning,
  layout = "vertical",
  renderFooter,
}: BaseControllerFieldProps & ControllerField) => {
  if (!id) {
    id = name;
  }

  const idHelp = `${id}-help`;

  return (
    <ConnectForm>
      {({ control }) => (
        <Controller
          control={control}
          name={name}
          render={({ field, fieldState, formState }) => {
            let finalHelpText = helpText;
            if (fieldState.error && fieldState.invalid) {
              finalHelpText = fieldState.error.message;
            }

            return (
              <span
                className={cn(
                  "flex",
                  {
                    "flex-col": layout === "vertical",
                  },
                  className,
                )}
              >
                {label && (
                  <label
                    className={cn("mb-[10px] text-sm font-medium", labelClassName)}
                    htmlFor={id}
                  >
                    {label} {required && <span className="text-urbe-errorValidation">*</span>}
                  </label>
                )}

                {render({ field, fieldState, formState, idHelp, id })}

                {renderFooter &&
                  renderFooter({
                    error: fieldState.error?.message,
                    helpText,
                    invalid: fieldState.invalid || !!invalid,
                    idHelp,
                    fieldValue: field.value,
                  })}

                {!renderFooter && finalHelpText && (
                  <HelpText
                    className="mt-[6px]"
                    id={idHelp}
                    invalid={fieldState.invalid || invalid}
                    warning={warning}
                  >
                    {finalHelpText}
                  </HelpText>
                )}
              </span>
            );
          }}
        />
      )}
    </ConnectForm>
  );
};

export default BaseControllerField;
